import { ArcadeEnvironment, IEnvironmentConstants } from 'scripts/util/constants/environment.interfaces';
import { IAdvantageProductAuthList, IAdvantageService } from 'scripts/api/advantage/advantage.service';
import {
  ITrackingEventRequest,
  TrackingClickType,
  TrackingTriggerType,
} from 'scripts/api/tracking/tracking.interfaces';
import { formatTrackingString, getPlacement } from 'scripts/util/tracking/tracking-helper';

import { AdvantageConfig } from '@rally/advantage-components/dist/types/models/advantageConfig.interface';
import { AdvantageProfile } from '@rally/advantage-components/dist/types/models/advantageProfile.interface';
import { AmpEventTrigger } from './../../api/tracking/amplitude.interfaces';
import { IAmpEventData } from 'scripts/api/tracking/amplitude.interfaces';
import { ILink } from 'scripts/api/api.interfaces';
import { IRootScope } from 'scripts/arcade.module.interfaces';
import { ITrackingService } from 'scripts/api/tracking/tracking.service';
import { Observable } from 'rxjs';
import advantageChromeContainerTemplate from 'views/advantage/advantage-chrome-container.html';
import angular from 'angular';
import { getLocale } from 'scripts/util/locale/locale';
import { goTo } from 'scripts/util/link/link';
import { defineCustomElements } from '@rally/advantage-components/loader';
import { Dictionary } from 'scripts/util/constants/i18n.constants';

export enum AdvantageChromeActiveItem {
  HealthPlan = 'healthplan',
}

export enum AdvantageAuthorizations {
  HealthPlan = 'advantage.healthplan',
  RallyRx = 'advantage.rallyrx',
}

interface IAdvantageSubNavTab extends ILink {
  active?: boolean;
  elementName: string;
}

interface IAdvChromeEvent extends Event {
  detail: {
    href: string;
    trigger: {
      'Trigger UI Event': string;
      'Trigger UI Element Name': string;
    };
  };
}

interface IAdvChromeEventData extends IAmpEventData {
  href?: string;
  eventTrigger?: AmpEventTrigger;
  target?: HTMLElement;
  track?: string;
}
/**
 * Defines the expected interface for the AC-CHROME DOM element.
 */
interface IAdvantageChrome {
  /**
   * a property that determines if the user is authorized to see the current page
   */
  isAuthorizedForPage: boolean;
  /**
   * A property used to set all profile-related element properties.
   */
  profile: AdvantageProfile;
  /**
   * The Advantage menu configuration.
   */
  config: AdvantageConfig;
  /**
   * The string containing the URI for the profile's Rally avatar.
   */
  avatarSrc?: string;
  /**
   * The count of the user's Rally coins.
   */
  coins?: number;
  /**
   * The count of messages associated with the profile.
   */
  unreadCount?: number;
  /**
   * The Rally user name associated with the profile.
   */
  displayName: string;
}

/**
 * Handles setup and interaction with the Advantage Header component.
 */
export class AdvantageChromeContainerController implements ng.IComponentController {
  public advantageSubNavTabs: IAdvantageSubNavTab[];
  /**
   * A reference to the Advantage Chrome component.
   */
  public chrome: IAdvantageChrome;
  /**
   * The current locale, from language selection.
   */
  public currentLocale: string;
  /**
   * The Advantage Chrome active item (active tab).
   */
  public activeItem: string;
  /**
   * The Advantage Chrome mobile slot name.
   */
  public mobileSlot: string;
  /**
   * A flag passed to AC-Chrome that, when true, triggers the advantage theme to display the unauthorized page.
   */
  public isAuthorizedForPage: boolean;
  /**
   * A flag that, when true, indicates that the component is being rendered as part of the unauthorized response.
   */
  public unauthorized?: boolean;
  private ie11TextCorrectionRetryInterval;

  constructor(
    private $location: ng.ILocationService,
    private $rootScope: IRootScope,
    public $state: ng.ui.IStateService,
    private $translate: ng.translate.ITranslateService,
    private $translatePartialLoader: ng.translate.ITranslatePartialLoaderService,
    private $window: ng.IWindowService,
    private Environment: IEnvironmentConstants,
    private advantageService: IAdvantageService,
    private trackingService: ITrackingService,
  ) {
    'ngInject';
    defineCustomElements(window);
    this.activeItem = AdvantageChromeActiveItem.HealthPlan;
    this.mobileSlot = `mobile-${this.activeItem}-subnav`;
    this.advantageSubNavTabs = this.initializeSubNav();
    this.$rootScope.$on('$stateChangeSuccess', () => {
      this.advantageSubNavTabs = this.initializeSubNav();
    });
    this.isAuthorizedForPage = this.unauthorized !== true;
    this.$translatePartialLoader.addPart(Dictionary.COMMON);
  }

  /**
   * Configures the Advantage header with the user profile and the current Advantage config.
   */
  public $onInit(): void {
    this.chrome = this.getAdvantageChrome();
    // FIXME bug in Advantage Header/Footer.  When fixed, remove dummy config.
    const dummyConfig = {
      header: {
        nav: {},
      },
    };
    dummyConfig.header.nav[AdvantageChromeActiveItem.HealthPlan] = true;

    this.chrome.config = (dummyConfig as unknown) as AdvantageConfig;
    this.currentLocale = getLocale().id;
    const config$ = this.advantageService.getAdvantageUiConfig(this.currentLocale).map(rsp => rsp.data);
    const productAuthList$ = this.advantageService.getAdvantageProductAuthList().map(rsp => rsp.data);
    const profile$ = this.advantageService.getAdvantageProfile().map(rsp => rsp.data);
    Observable.zip(config$, productAuthList$, profile$).subscribe(
      ([config, productAuthList, profile]) => this.initAdvantageChrome(config, productAuthList, profile),
      err => {
        console.warn(err);
        // Calls to the advantage endpoints are not working locally, this uses mock data to enable
        // local development until the endpoints work locally
        if (this.Environment.CONFIG.ARCADE_WEB_ENVIRONMENT_NAME === ArcadeEnvironment.Local) {
          // eslint-disable-next-line
          const config = { "help": { "text": { "short": "Help Center & FAQs", "long": "Help Center & FAQs" }, "target": "_blank", "url": "https://helpcenter.werally.com/rally/s/?partner=rally&affiliation=rally_simon_aff_20190626_152634_8J4Z&client=simon", "meta": { "name": "help", "icon": "help" }, "id": "help" }, "notifications": { "text": { "short": "Notifications", "long": "Notifications" }, "target": "_self", "url": "https://health.dev.werally.in/messages/list/all/", "meta": { "name": "notifications", "icon": "notification" }, "id": "notifications", "environmentalParams": { "path": "messages/list/all/", "subdomain": "health" } }, "header": { "logo": { "link": { "text": { "short": "Logo", "long": "Logo" }, "target": "_self", "url": "https://home.dev.werally.in/en-US/", "meta": { "name": "rally", "section": "header logo" }, "id": "logo", "environmentalParams": { "path": "", "subdomain": "home" }, "hideSubnavigationMenu": false }, "imageAltText": "Rally Logo" }, "accountMenu": [{ "text": { "short": "Profile", "long": "Profile" }, "target": "_self", "url": "https://health.dev.werally.in/profile/health", "meta": { "name": "profile", "section": "header account-menu", "icon": "profile" }, "id": "advantage_profile_menu_link", "environmentalParams": { "path": "profile/health", "subdomain": "health" } }, { "text": { "short": "Settings", "long": "Settings" }, "target": "_self", "url": "https://health.dev.werally.in/settings", "meta": { "name": "settings", "section": "header account-menu", "icon": "settings" }, "id": "advantage_settings_menu_link", "environmentalParams": { "path": "settings", "subdomain": "health" } }, { "text": { "short": "Security", "long": "Security" }, "target": "_blank", "url": "https://accounts.dev.werally.in/settings", "meta": { "name": "security", "section": "header account-menu", "icon": "security" }, "id": "advantage_security_menu_link", "environmentalParams": { "path": "settings", "subdomain": "accounts" } }, { "text": { "short": "Log Out", "long": "Log Out" }, "target": "_self", "url": "https://accounts.dev.werally.in/lo", "meta": { "name": "logout", "section": "header account-menu", "icon": "logOut" }, "id": "advantage_logout_menu_link", "environmentalParams": { "path": "lo", "subdomain": "accounts" } }], "nav": { "advantage_dashboard": { "text": { "short": "Home", "long": "Home" }, "target": "_self", "url": "https://home.dev.werally.in/en-US/", "meta": { "name": "home", "section": "header nav", "isIntermediary": false, "icon": "advantage" }, "id": "advantage_tab_link", "environmentalParams": { "path": "", "subdomain": "home" }, "hideSubnavigationMenu": false }, "care": { "text": { "short": "Find Care", "long": "Find Care & Costs" }, "target": "_self", "url": "https://home.dev.werally.in/en-US/care", "meta": { "name": "care", "section": "header nav", "isIntermediary": true, "icon": "care" }, "id": "care_tab_link", "environmentalParams": { "path": "care", "subdomain": "home" }, "hideSubnavigationMenu": false }, "healthplan": { "text": { "short": "Health Plan", "long": "Health Plan" }, "target": "_self", "url": "https://healthplan.ecstatic-ride.rally-dev/dashboard", "meta": { "name": "healthplan", "section": "header nav", "isIntermediary": false, "icon": "healthplan" }, "id": "arcade_tab_link", "environmentalParams": { "path": "dashboard", "subdomain": "healthplan" }, "hideSubnavigationMenu": false }, "wellness": { "text": { "short": "Wellness", "long": "Health & Wellness" }, "target": "_self", "url": "https://health.dev.werally.in/dashboard", "meta": { "name": "wellness", "section": "header nav", "isIntermediary": false, "icon": "engage" }, "id": "engage_tab_link", "environmentalParams": { "path": "dashboard", "subdomain": "health" }, "hideSubnavigationMenu": false }, "rewards": { "text": { "short": "Rewards", "long": "Rewards" }, "target": "_self", "url": "https://rewards.dev.werally.in/", "meta": { "name": "rewards", "section": "header nav", "isIntermediary": false, "icon": "rewards" }, "id": "rewards_tab_link", "environmentalParams": { "path": "", "subdomain": "rewards" }, "hideSubnavigationMenu": false }, "resources": { "text": { "short": "Resources", "long": "Resources" }, "target": "_self", "url": "https://resources.dev.werally.in/", "meta": { "name": "resources", "section": "header nav", "isIntermediary": false, "icon": "resources" }, "id": "resources_tab_link", "environmentalParams": { "path": "", "subdomain": "resources" }, "hideSubnavigationMenu": false } } }, "footer": { "corporate": [{ "text": { "short": "Terms", "long": "Terms" }, "target": "_blank", "url": "https://health.dev.werally.in/corporate/terms/us", "meta": { "name": "terms", "section": "footer nav" }, "id": "corporate_terms", "environmentalParams": { "path": "corporate/terms/us", "subdomain": "health" } }, { "text": { "short": "Privacy", "long": "Privacy" }, "target": "_blank", "url": "https://health.dev.werally.in/corporate/privacy", "meta": { "name": "privacy", "section": "footer nav" }, "id": "corporate_privacy", "environmentalParams": { "path": "corporate/privacy", "subdomain": "health" } }, { "text": { "short": "HIPAA", "long": "HIPAA" }, "target": "_blank", "url": "https://health.dev.werally.in/corporate/hipaa", "meta": { "name": "hipaa", "section": "footer nav" }, "id": "corporate_hipaa", "environmentalParams": { "path": "corporate/hipaa", "subdomain": "health" } }, { "text": { "short": "Rally Rewards Rules", "long": "Rally Rewards Rules" }, "target": "_blank", "url": "https://health.dev.werally.in/corporate/sweepstakes/us", "meta": { "name": "rules", "section": "footer nav" }, "id": "corporate_rules", "environmentalParams": { "path": "corporate/sweepstakes/us", "subdomain": "health" } }, { "text": { "short": "Accessibility", "long": "Accessibility" }, "target": "_blank", "url": "https://www.rallyhealth.com/accessibility-statement", "meta": { "name": "accessibility", "section": "footer nav" }, "id": "corporate_accessibility" }], "footerSections": { "advantage_dashboard": { "id": "advantage_footersection", "heading": { "short": "Quick Links", "long": "Quick Links" }, "links": { "resources": { "text": { "short": "Resources", "long": "Resources" }, "target": "_self", "url": "https://resources.dev.werally.in/", "meta": { "name": "Rally_resources_/", "section": "footer nav", "isIntermediary": false }, "id": "advantage_dashboard_Resources_footer_link", "environmentalParams": { "path": "", "subdomain": "resources" } }, "my_profile": { "text": { "short": "My Profile", "long": "My Profile" }, "target": "_self", "url": "https://health.dev.werally.in/profile/health", "meta": { "name": "Rally_wellness_/profile/health", "section": "footer nav", "isIntermediary": false }, "id": "advantage_dashboard_My_Profile_footer_link", "environmentalParams": { "path": "profile/health", "subdomain": "health" } } } }, "wellness": { "id": "engage_footersection", "heading": { "short": "Wellness", "long": "Health & Wellness" }, "links": { "dashboard": { "text": { "short": "Dashboard", "long": "Dashboard" }, "target": "_self", "url": "https://health.dev.werally.in/dashboard", "meta": { "name": "Rally_wellness_/dashboard", "section": "footer nav", "isIntermediary": false }, "id": "wellness_Dashboard_footer_link", "environmentalParams": { "path": "dashboard", "subdomain": "health" } }, "missions": { "text": { "short": "Missions", "long": "Missions" }, "target": "_self", "url": "https://health.dev.werally.in/missions", "meta": { "name": "Rally_wellness_/missions", "section": "footer nav", "isIntermediary": false }, "id": "wellness_Missions_footer_link", "environmentalParams": { "path": "missions", "subdomain": "health" } }, "challenges": { "text": { "short": "Challenges", "long": "Challenges" }, "target": "_self", "url": "https://health.dev.werally.in/challenges", "meta": { "name": "Rally_wellness_/challenges", "section": "footer nav", "isIntermediary": false }, "id": "wellness_Challenges_footer_link", "environmentalParams": { "path": "challenges", "subdomain": "health" } }, "communities": { "text": { "short": "Communities", "long": "Communities" }, "target": "_self", "url": "https://health.dev.werally.in/communities", "meta": { "name": "Rally_wellness_/communities", "section": "footer nav", "isIntermediary": false }, "id": "wellness_Communities_footer_link", "environmentalParams": { "path": "communities", "subdomain": "health" } } } }, "rewards": { "id": "rewards_footersection", "heading": { "short": "Rewards", "long": "Rewards" }, "links": { "rally_rewards": { "text": { "short": "Rally Rewards", "long": "Rally Rewards" }, "target": "_self", "url": "https://rewards.dev.werally.in/rally-rewards", "meta": { "name": "Rally_rewards_/rally-rewards", "section": "footer nav", "isIntermediary": false }, "id": "rewards_Rally_Rewards_footer_link", "environmentalParams": { "path": "rally-rewards", "subdomain": "rewards" } }, "marketplace": { "text": { "short": "Marketplace", "long": "Marketplace" }, "target": "_self", "url": "https://rewards.dev.werally.in/marketplace", "meta": { "name": "Rally_rewards_/marketplace", "section": "footer nav", "isIntermediary": false }, "id": "rewards_Marketplace_footer_link", "environmentalParams": { "path": "marketplace", "subdomain": "rewards" } } } } } } } as any;
          const productAuthList = {
            advantageAuthorizations: [AdvantageAuthorizations.HealthPlan, AdvantageAuthorizations.RallyRx],
          };
          // eslint-disable-next-line
          const profile = { "affiliation": "optum_default_se_small_business_prime", "avatar": "https://s3.amazonaws.com/rally-non-prod/avatars/1a0498ab-e74a-4390-9a8a-fad582822bbb.png", "medicalPlanCarrier": "united_health_care", "client": "optum_default", "coins": 1000, "isImpersonator": false, "messageCount": 0, "partner": "optum", "profileName": "FoxyCatLady", "rallyId": "1a0498ab-e74a-4390-9a8a-fad582822bbb", "sessionId": "a080eff9-7e9a-47cb-a606-fa8b4bdd60d8" } as any;
          this.initAdvantageChrome(config, productAuthList, profile);
        }
      },
    );
    // we want a raw Element and not IAdvantageChrome wrapped one because you can't use addEventListener on just IAdvantageChrome
    const advantageChromeElement: Element = document.getElementsByTagName('ac-chrome')[0];
    advantageChromeElement.addEventListener('chromeTrackingEvent', e =>
      this.ChromeTrackingCallback(this.convertToAdvChromeEventData(e as IAdvChromeEvent), advantageChromeElement),
    );
  }

  public initAdvantageChrome(
    config: AdvantageConfig,
    productAuthList: IAdvantageProductAuthList,
    profile: AdvantageProfile,
  ): void {
    if (productAuthList && productAuthList.advantageAuthorizations) {
      // If Arcade-UI is rendering the unauthorized page, trigger the Advantage unauthorized page.
      this.isAuthorizedForPage =
        this.unauthorized !== true &&
        productAuthList.advantageAuthorizations.indexOf(AdvantageAuthorizations.HealthPlan) !== -1;
      if (productAuthList.advantageAuthorizations.indexOf(AdvantageAuthorizations.RallyRx) !== -1) {
        const rallyRxSubNavTab = {
          active: false,
          href: this.Environment.CONFIG.ARCADE_WEB_RALLY_RX_ADVANTAGE_URL,
          text: 'PRESCRIPTIONS',
          elementName: 'nav_prescriptions',
        };
        this.advantageSubNavTabs.push(rallyRxSubNavTab);

        this.ie11TextCorrectionRetryInterval = setInterval(this.initializePrescriptionsTabText.bind(this), 1000);
      }
    } else {
      this.isAuthorizedForPage = false;
    }
    this.chrome.config = config;
    this.chrome.coins = profile.coins;
    this.chrome.avatarSrc = profile.avatar;
    this.chrome.displayName = profile.profileName;
    this.chrome.unreadCount = profile.messageCount;
  }

  // IE11 hack to get the prescription tab text to show as "PRESCRIPTIONS" rather than the literal "{{subNavTab.text}}"
  private initializePrescriptionsTabText(): void {
    try {
      const prescriptionsSubnavLink = document.getElementById('PRESCRIPTIONS');
      const supportsShadowDom = prescriptionsSubnavLink.shadowRoot;
      if (!supportsShadowDom) {
        if (prescriptionsSubnavLink.children[0].tagName === 'A') {
          prescriptionsSubnavLink.children[0].innerHTML = this.$translate.instant('PRESCRIPTIONS');
        } else {
          return;
        }
      }
      clearInterval(this.ie11TextCorrectionRetryInterval);
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Returns true if the tab identified by subNavTabStateName matches the current state to indicate
   * that a tab is currently selected.
   * @param subNavTabStateName The state name for the selected tab.
   */
  public isAdvantageSubNavActive(subNavTabStateName: string): boolean {
    return this.$state.current.name === subNavTabStateName;
  }

  /**
   * Returns the Advantage Chrome Component as an [[IAdvantageChrome]] instance.
   * The Advantage Chrome Component has two properties we are interested in,
   * config and profile.
   */
  private getAdvantageChrome(): IAdvantageChrome {
    const chrome = angular.element('ac-chrome')[0];
    return chrome && ((chrome as unknown) as IAdvantageChrome);
  }

  private ChromeTrackingCallback(event: IAdvChromeEventData, advantageChromeElement: Element): void {
    const isLink = event.href !== undefined;
    let isInternalRedirect;
    let isExternalLink;
    let anchor;
    if (isLink) {
      anchor = document.createElement('a');
      anchor.href = event.href;
      isInternalRedirect = anchor.pathname.split('/').pop() === 'internal-redirect';
      isExternalLink = isInternalRedirect || anchor.hostname !== this.$window.location.hostname;
    }

    const advantageChromeTrackingEventRequest = this.getAdvChromeTrackingEventRequest(event);
    if (isLink && isExternalLink) {
      advantageChromeTrackingEventRequest.clickType = TrackingClickType.ExternalLink;
      this.setAdvChromeTrackingEventExternalLinkProps(advantageChromeTrackingEventRequest, anchor, isInternalRedirect);
      this.trackingService.queueEvent(advantageChromeTrackingEventRequest);
      this.trackingService.postEvents().subscribe();
    } else {
      advantageChromeTrackingEventRequest.clickType = isLink
        ? TrackingClickType.StateChange
        : TrackingClickType.PageEvent;
      this.trackingService.queueEvent(advantageChromeTrackingEventRequest);
      if (advantageChromeTrackingEventRequest.clickType === TrackingClickType.StateChange) {
        // if the clickType in the header/footer is a state change we are handing control over to the ui-router
        // for proper amplitude tracking, and to not trigger a full reload. the click event below fires directly after the
        // tracking event is emitted
        const stateChangeClickListener = (event: MouseEvent): void => {
          event.preventDefault();
          advantageChromeElement.removeEventListener('click', stateChangeClickListener);
          goTo(anchor.href);
        };
        advantageChromeElement.addEventListener('click', stateChangeClickListener);
      }
    }
  }

  private initializeSubNav(): IAdvantageSubNavTab[] {
    return [
      {
        active: this.isAdvantageSubNavActive('authenticated.dashboard'),
        href: '/dashboard',
        text: 'SUMMARY',
        elementName: 'nav_summary',
      },
      {
        active: this.isAdvantageSubNavActive('authenticated.claimsAndAccounts.planBalances'),
        href: '/plan-balances?accountsExpanded=1',
        text: 'PLAN_BALANCES',
        elementName: 'nav_plan_balances',
      },
      {
        active:
          this.isAdvantageSubNavActive('authenticated.claimsAndAccounts.claims') ||
          this.isAdvantageSubNavActive('authenticated.claimsAndAccounts.claimDetails'),
        href: '/claims',
        text: 'CLAIMS',
        elementName: 'nav_claims',
      },
      {
        active: this.isAdvantageSubNavActive('authenticated.claimsAndAccounts.submitClaim'),
        href: '/submit-claim',
        text: 'SUBMIT_CLAIM',
        elementName: 'nav_submit_claim',
      },
      {
        active: this.isAdvantageSubNavActive('authenticated.claimsAndAccounts.statements'),
        href: '/statements',
        text: 'STATEMENTS',
        elementName: 'nav_statements',
      },
    ];
  }

  public setAdvChromeTrackingEventExternalLinkProps(
    event: ITrackingEventRequest,
    anchor: HTMLAnchorElement,
    isInternalRedirect: boolean,
  ): void {
    if (isInternalRedirect) {
      const decodedUrl = decodeURIComponent(anchor.href.split('deepLink=').pop());
      const redirectUrlAnchor = document.createElement('a');
      redirectUrlAnchor.href = decodedUrl;
      event.externalDomain = redirectUrlAnchor.hostname;
      event.externalUrl = decodedUrl.split('?')[0];
    } else {
      event.externalDomain = anchor.hostname;
      event.externalUrl = anchor.href.split('?')[0];
    }
  }

  private getAdvChromeTrackingEventRequest(event: IAdvChromeEventData): ITrackingEventRequest {
    const element = event.target;
    const placement = getPlacement(element);
    const actionName = formatTrackingString(event.track);

    const trackingEvent = {
      trigger: TrackingTriggerType.Click,
      uri: this.$location.url(),
      serviceVersion: this.Environment.CONFIG.ARCADE_WEB_VERSION,
      featureList: [],
      actionName,
      placement,
    } as ITrackingEventRequest;

    return trackingEvent;
  }

  private convertToAdvChromeEventData(event: IAdvChromeEvent): IAdvChromeEventData {
    return {
      href: event.detail.href,
      eventTrigger: event.detail.trigger['Trigger UI Event'],
      track: event.detail.trigger['Trigger UI Element Name'],
      target: event.target,
    } as IAdvChromeEventData;
  }
}

export class AdvantageChromeContainerComponent implements ng.IComponentOptions {
  public bindings: any;
  public controller: any;
  public templateUrl: string;
  public transclude = true;

  constructor() {
    this.controller = AdvantageChromeContainerController;
    this.templateUrl = advantageChromeContainerTemplate;
    this.bindings = {
      unauthorized: '=',
    };
  }
}
